<template>
    <div
        v-if="show"
        class="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
        :style="modalBackdropStyle"
        @click="handleBackdropClick"
    >
        <div
            class="relative w-full max-w-md mx-auto backdrop-blur-2xl border rounded-2xl sm:rounded-3xl p-6 sm:p-8 animate-scale-in"
            :style="modalStyle"
            @click.stop
        >
            <button
                @click="closeModal"
                class="absolute top-4 right-4 text-white/50 hover:text-white transition-all p-2 rounded-xl hover:bg-white/10 active:scale-95"
                :disabled="connecting"
            >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            </button>

            <div v-if="connecting" class="text-center py-12">
                <div class="w-20 h-20 mx-auto mb-6 border-4 border-white/20 rounded-full animate-spin"
                     :style="{ borderTopColor: themeColors.primary }"></div>
                <h3 class="text-2xl font-black mb-3 text-white tracking-tight">{{ connectingMessage }}</h3>
                <p class="text-white/60 text-base mb-6">{{ statusMessage }}</p>

                <div v-if="selectedWallet"
                     class="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-white/5 border border-white/10">
                    <div v-if="selectedWallet === 'email'" class="w-10 h-10 rounded-xl flex items-center justify-center"
                         :style="{ background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)` }">
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                        </svg>
                    </div>
                    <img v-else :src="getWalletIcon(selectedWallet)" alt="Wallet Icon" class="wallet-svg" />
                    <span class="font-bold text-base" :style="{ color: themeColors.primary }">
                        {{ selectedWallet === 'email' ? t('walletModalEmailLogin') : getWalletName(selectedWallet) }}
                    </span>
                </div>
            </div>

            <div v-else-if="error" class="text-center py-12">
                <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-500/20 flex items-center justify-center">
                    <svg class="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                </div>
                <h3 class="text-2xl font-black mb-3 text-white tracking-tight">{{ t('walletModalConnectionFailed') }}</h3>
                <p class="text-red-400 text-base mb-8 leading-relaxed max-w-sm mx-auto">{{ error }}</p>
                <button
                    @click="retry"
                    class="px-8 py-3 rounded-xl font-bold text-base transition-all hover:scale-105 active:scale-95"
                    :style="{
                        background: `rgba(${themeColors.primaryRgb}, 0.2)`,
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: themeColors.primary,
                        color: themeColors.primary
                    }"
                >
                    {{ t('walletModalTryAgain') }}
                </button>
            </div>

            <div v-else>
                <!-- Email Login/Register Form -->
                <div v-if="showEmailForm">
                    <button
                        @click="backToWallets"
                        class="mb-6 flex items-center gap-2 text-white/70 hover:text-white transition-all text-sm font-medium"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                        {{ t('walletModalBackToWallets') || 'Back to options' }}
                    </button>

                    <div class="text-center mb-6">
                        <div class="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                             :style="modalIconStyle">
                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                        </div>
                        <h3 class="text-2xl font-black mb-2 text-white tracking-tight">
                            {{ authMode === 'login' ? (t('walletModalLoginTitle') || 'Sign In to Your Account') : (t('walletModalRegisterTitle') || 'Create Your Account') }}
                        </h3>
                        <p class="text-white/70 text-sm leading-relaxed font-light">
                            {{ authMode === 'login' ? (t('walletModalLoginDesc') || 'Enter your credentials to access your account') : (t('walletModalRegisterDesc') || 'Join our platform and start your investment journey') }}
                        </p>
                    </div>

                    <form @submit.prevent="handleEmailAuth" class="space-y-4">
                        <div>
                            <label class="block text-sm font-semibold mb-2 text-white/90">
                                {{ t('email') || 'Email Address' }}
                            </label>
                            <input
                                v-model="email"
                                type="email"
                                required
                                class="w-full px-4 py-3 rounded-xl border transition-all text-white placeholder-white/40 focus:outline-none focus:ring-2"
                                :class="emailErrors.email ? 'border-red-500 bg-red-500/10' : 'border-white/20 bg-white/5 focus:border-white/40'"
                                :placeholder="t('emailPlaceholder') || 'Enter your email'"
                            >
                            <p v-if="emailErrors.email" class="mt-2 text-sm text-red-400">{{ emailErrors.email }}</p>
                        </div>

                        <div>
                            <label class="block text-sm font-semibold mb-2 text-white/90">
                                {{ t('password') || 'Password' }}
                            </label>
                            <div class="relative">
                                <input
                                    v-model="password"
                                    :type="showPassword ? 'text' : 'password'"
                                    required
                                    class="w-full px-4 py-3 pr-12 rounded-xl border transition-all text-white placeholder-white/40 focus:outline-none focus:ring-2"
                                    :class="emailErrors.password ? 'border-red-500 bg-red-500/10' : 'border-white/20 bg-white/5 focus:border-white/40'"
                                    :placeholder="t('passwordPlaceholder') || 'Enter your password'"
                                >
                                <button
                                    type="button"
                                    @click="showPassword = !showPassword"
                                    class="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-all"
                                >
                                    <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                                    </svg>
                                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                    </svg>
                                </button>
                            </div>
                            <p v-if="emailErrors.password" class="mt-2 text-sm text-red-400">{{ emailErrors.password }}</p>
                        </div>

                        <div v-if="authMode === 'register'">
                            <label class="block text-sm font-semibold mb-2 text-white/90">
                                {{ t('confirmPassword') || 'Confirm Password' }}
                            </label>
                            <div class="relative">
                                <input
                                    v-model="passwordConfirmation"
                                    :type="showConfirmPassword ? 'text' : 'password'"
                                    required
                                    class="w-full px-4 py-3 pr-12 rounded-xl border transition-all text-white placeholder-white/40 focus:outline-none focus:ring-2"
                                    :class="emailErrors.password_confirmation ? 'border-red-500 bg-red-500/10' : 'border-white/20 bg-white/5 focus:border-white/40'"
                                    :placeholder="t('confirmPasswordPlaceholder') || 'Confirm your password'"
                                >
                                <button
                                    type="button"
                                    @click="showConfirmPassword = !showConfirmPassword"
                                    class="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-all"
                                >
                                    <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                                    </svg>
                                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                    </svg>
                                </button>
                            </div>
                            <p v-if="emailErrors.password_confirmation" class="mt-2 text-sm text-red-400">{{ emailErrors.password_confirmation }}</p>
                            <p v-else class="mt-2 text-xs text-white/50">{{ t('passwordRequirements') || 'Min 8 characters with uppercase, lowercase, number & special character' }}</p>
                        </div>

                        <button
                            type="submit"
                            :disabled="connecting"
                            class="w-full py-3 rounded-xl font-bold text-base transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                            :style="{
                                background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)`,
                                color: '#ffffff'
                            }"
                        >
                            {{ authMode === 'login' ? (t('signIn') || 'Sign In') : (t('createAccount') || 'Create Account') }}
                        </button>

                        <div v-if="registrationEnabled" class="text-center pt-2">
                            <button
                                type="button"
                                @click="toggleAuthMode"
                                class="text-sm text-white/70 hover:text-white transition-all"
                            >
                                {{ authMode === 'login' 
                                    ? (t('dontHaveAccount') || "Don't have an account?") + ' ' + (t('signUpHere') || 'Create one now')
                                    : (t('alreadyHaveAccount') || 'Already have an account?') + ' ' + (t('signInHere') || 'Sign in here')
                                }}
                            </button>
                        </div>
                    </form>
                </div>

                <!-- Wallet Selection -->
                <div v-else>
                    <div class="text-center mb-8">
                        <div
                            class="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center animate-pulse-slow"
                            :style="modalIconStyle">
                            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                            </svg>
                        </div>
                        <h3 class="text-2xl sm:text-3xl font-black mb-3 text-white tracking-tight leading-tight">
                            {{ t('walletModalConnectWalletTitle') || 'Connect Your Wallet' }}
                        </h3>
                        <p class="text-white/70 text-base leading-relaxed font-light">
                            {{ t('walletModalConnectWalletDesc') || 'Choose your preferred connection method' }}
                        </p>
                    </div>

                    <div class="space-y-3">
                        <button
                            v-for="wallet in availableWallets"
                            :key="wallet.type"
                            @click="connectWallet(wallet.type)"
                            :disabled="connecting || isProcessing"
                            class="group w-full flex items-center gap-4 p-4 rounded-xl border transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                            :style="{
                                background: 'rgba(255, 255, 255, 0.05)',
                                borderColor: 'rgba(255, 255, 255, 0.1)'
                            }"
                        >
                            <div v-if="wallet.type === 'email'" class="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                 :style="{ background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryDark} 100%)` }">
                                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                </svg>
                            </div>
                            <img v-else :src="wallet.icon" :alt="wallet.name" class="wallet-svg" />
                            <div class="flex-1 text-left">
                                <div class="font-bold text-base text-white tracking-tight">{{ wallet.name }}</div>
                                <div class="text-sm text-white/60 font-light">{{ wallet.description }}</div>
                            </div>
                            <svg class="w-5 h-5 text-white/40 group-hover:text-white/60 transition-colors" fill="none"
                                 stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    </div>

                    <p class="text-xs text-white/40 text-center mt-6 leading-relaxed font-light">
                        {{ t('walletModalTerms') || 'By connecting, you agree to our Terms of Service and Privacy Policy' }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {usePage} from '@inertiajs/vue3'
import {useTranslation} from '@/composables/useTranslation'
import {useToast} from "@/composables/useToast.js";
import {createAppKit} from '@reown/appkit'
import {EthersAdapter} from '@reown/appkit-adapter-ethers'
import {mainnet, bsc, polygon, arbitrum, optimism, base} from '@reown/appkit/networks'

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    registrationEnabled: {
        type: Boolean,
        default: true
    },
    manualAuthEnabled: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits(['close', 'success'])

const page = usePage()
const {t} = useTranslation()
const { showToast } = useToast();
const primaryColor = computed(() => page.props.primaryColor || '#86efac')

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172}
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172}
}

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)
}

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex)
    const newR = Math.min(255, Math.max(0, rgb.r + amount))
    const newG = Math.min(255, Math.max(0, rgb.g + amount))
    const newB = Math.min(255, Math.max(0, rgb.b + amount))
    return rgbToHex(newR, newG, newB)
}

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}))

const modalBackdropStyle = computed(() => ({
    backgroundColor: 'rgba(0, 0, 0, 0.8)'
}))

const modalStyle = computed(() => ({
    background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(${themeColors.value.primaryRgb}, 0.1)`
}))

const modalIconStyle = computed(() => ({
    background: `linear-gradient(135deg, ${themeColors.value.primary} 0%, ${themeColors.value.primaryDark} 100%)`
}))

const walletIcons = {
    metamask: '/assets/metamask.svg',
    walletconnect: '/assets/walletconnect.svg'
}

const walletConnectProjectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || ''

const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '')
const hasMetaMaskProvider = () => !!window.ethereum?.isMetaMask
const isMetaMaskMobileBrowser = () => isMobileDevice() && hasMetaMaskProvider()
const shouldShowMetaMask = () => (!isMobileDevice() && hasMetaMaskProvider()) || isMetaMaskMobileBrowser() || isMobileDevice()

const buildMetaMaskDescription = () => isMobileDevice()
    ? (t('walletModalMetaMaskMobile') || 'Open in MetaMask app or browser')
    : (t('walletModalMetaMaskDesc') || 'Connect using MetaMask wallet')

const connecting = ref(false)
const error = ref(null)
const selectedWallet = ref('')
const connectingMessage = ref(t('walletModalConnecting'))
const statusMessage = ref(t('walletModalConfirmWallet'))
const isProcessing = ref(false)
const availableWallets = ref([])
const modal = ref(null)
const isModalOpen = ref(false)
const showEmailForm = ref(false)
const authMode = ref('login')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const emailErrors = ref({})

const walletConfigs = [
    {
        type: 'email',
        name: t('walletModalEmailLogin') || 'Manual Login',
        description: t('walletModalEmailDesc') || 'Authenticate using your email credentials',
        provider: () => null,
        detectCondition: () => true,
        icon: walletIcons.walletconnect // Will use custom SVG in template
    },
    {
        type: 'walletconnect',
        name: 'Web3 Connect',
        description: t('walletModalWalletConnectDesc') || 'Connect with 300+ supported wallets',
        provider: () => modal.value,
        detectCondition: () => true,
        icon: walletIcons.walletconnect
    }
]

const openMetaMaskDeepLink = () => {
    const sanitizedUrl = window.location.href.replace(/^https?:\/\//, '')
    const deepLink = `https://metamask.app.link/dapp/${sanitizedUrl}`

    connectingMessage.value = t('walletModalOpeningMetaMask') || 'Opening MetaMask...'
    statusMessage.value = t('walletModalRedirectingToApp') || 'Redirecting to MetaMask app...'

    sessionStorage.setItem('metamask_auto_connect', 'true')

    setTimeout(() => {
        window.location.href = deepLink
    }, 400)

    setTimeout(() => {
        isProcessing.value = false
        connecting.value = false
    }, 2000)
}

const detectAvailableWallets = () => {
    let filtered = walletConfigs.filter(wallet => {
        // Filter out email option if manual auth is disabled
        if (wallet.type === 'email' && !props.manualAuthEnabled) {
            return false
        }
        return wallet.detectCondition()
    })

    if (filtered.length === 0) {
        // Fallback to first non-email wallet if email is disabled
        const fallback = walletConfigs.find(w => w.type !== 'email')
        availableWallets.value = fallback ? [fallback] : []
    } else {
        availableWallets.value = filtered
    }
}

const autoConnectIfPending = () => {
    const shouldAutoConnect = sessionStorage.getItem('metamask_auto_connect') === 'true'

    if (shouldAutoConnect && hasMetaMaskProvider()) {
        sessionStorage.removeItem('metamask_auto_connect')
        connectWallet('metamask')
    }
}

const getWalletIcon = (type) => {
    return walletIcons[type] || walletIcons.metamask
}

const getWalletName = (type) => {
    const wallet = walletConfigs.find(w => w.type === type)
    return wallet?.name || 'Wallet'
}

const getReferralCode = () => {
    const params = new URLSearchParams(window.location.search)
    return params.get('ref') || localStorage.getItem('referral_code') || null
}

const initAppKit = async () => {
    if (modal.value) return modal.value

    if (!walletConnectProjectId) {
        throw new Error(t('walletModalWalletConnectProjectMissing') || 'WalletConnect project ID is missing. Set VITE_WALLETCONNECT_PROJECT_ID in your .env')
    }

    try {
        // Create Reown AppKit instance
        modal.value = createAppKit({
            adapters: [new EthersAdapter()],
            projectId: walletConnectProjectId,
            networks: [mainnet, bsc, polygon, arbitrum, optimism, base],
            defaultNetwork: mainnet,
            metadata: {
                name: page.props.appName || 'Web3 Finance',
                description: 'Professional Web3 Finance Platform',
                url: window.location.origin,
                icons: [`${window.location.origin}/assets/logo.png`]
            },
            features: {
                analytics: false,
                email: false,
                socials: false,
            },
            themeMode: 'dark',
            themeVariables: {
                '--w3m-accent': themeColors.value.primary,
                '--w3m-z-index': '9999',
            },
            enableWalletGuide: true,
            enableNetworkView: true,
            allowUriSchemes: ['metamask', 'trust', 'ledger', 'walletconnect'],
            termsConditionsUrl: `${window.location.origin}/terms`,
            privacyPolicyUrl: `${window.location.origin}/privacy`,
        })

        console.log('Reown AppKit initialized successfully')

        return modal.value
    } catch (err) {
        console.error('Reown AppKit initialization error:', err)

        const msg = (err?.message || '').toLowerCase()
        if (msg.includes('failed to fetch') || msg.includes('blocked by client')) {
            error.value = t('walletModalWalletConnectBlocked') || 'WalletConnect was blocked by a browser extension. Please disable ad/tracker blocking for this site.'
        }

        throw err
    }
}

const connectWallet = async (walletType) => {
    if (isProcessing.value || connecting.value) return

    if (walletType === 'email') {
        showEmailForm.value = true
        return
    }

    isProcessing.value = true
    connecting.value = true
    error.value = null
    selectedWallet.value = walletType

    connectingMessage.value = t('walletModalConnecting')
    statusMessage.value = t('walletModalConfirmWallet')

    try {
        if (walletType === 'walletconnect') {
            // Initialize Reown AppKit if not already done
            if (!modal.value) {
                console.log('Initializing AppKit modal...')
                await initAppKit()
            }
            
            console.log('Opening wallet connection modal...')
            // Clear any stale session before opening (best-effort)
            try { await disconnectWallet() } catch {}
            connectingMessage.value = t('walletModalOpeningWallet') || 'Opening wallet selector...'
            statusMessage.value = t('walletModalSelectWallet') || 'Select your wallet to connect'

            return new Promise((resolve) => {
                let stateChangeCount = 0
                let hasSeenOpen = false
                console.log('Subscribed to modal state changes, waiting for user action...')

                const unsubscribe = modal.value.subscribeState((state) => {
                    stateChangeCount++
                    console.log(`[State Change #${stateChangeCount}]`, {
                        open: state.open,
                        address: state.address,
                        selectedNetworkId: state.selectedNetworkId,
                        loading: state.loading
                    })

                    // Mark that the modal actually opened
                    if (state.open) {
                        hasSeenOpen = true
                        return
                    }

                    // Only handle close after we have seen it open
                    if (!state.open && !hasSeenOpen) {
                        return
                    }

                    console.log('Modal closed detected')

                    // Give a small delay to ensure state is settled
                    setTimeout(() => {
                        const finalState = modal.value.getState()
                        console.log('Final state after modal close:', finalState)

                        unsubscribe()
                        isModalOpen.value = false

                        if (finalState?.address) {
                            console.log('✓ Connection successful! Address:', finalState.address)
                            connectingMessage.value = t('walletModalConnected') || 'Wallet connected!'
                            statusMessage.value = t('walletModalAuthenticating') || 'Authenticating...'

                            // Get provider with multiple fallback methods
                            let provider = modal.value.getWalletProvider()
                            console.log('Provider from getWalletProvider():', provider ? '✓ Available' : '✗ NULL')

                            // Fallback 1: Try to get from adapter
                            if (!provider && modal.value.adapter) {
                                console.log('Trying adapter.getProvider()...')
                                provider = modal.value.adapter.getProvider?.()
                                console.log('Provider from adapter:', provider ? '✓ Available' : '✗ NULL')
                            }

                            // Fallback 2: Try window.ethereum (for MetaMask and similar)
                            if (!provider && window.ethereum) {
                                console.log('Using window.ethereum as fallback...')
                                provider = window.ethereum
                            }

                            // Fallback 3: Create a basic provider from the connection
                            if (!provider) {
                                console.log('Creating basic provider wrapper...')
                                // AppKit should have set up the connection, try to use it
                                provider = {
                                    request: async (args) => {
                                        if (window.ethereum) {
                                            return window.ethereum.request(args)
                                        }
                                        throw new Error('No provider available')
                                    }
                                }
                            }

                            console.log('Final provider status:', provider ? '✓ Ready' : '✗ Failed')

                            if (!provider) {
                                console.error('✗ All provider fallback methods failed')
                                isProcessing.value = false
                                connecting.value = false
                                error.value = 'Failed to get wallet provider. Please ensure your wallet is connected.'
                                return
                            }

                            console.log('Starting authentication process...')
                            directLoginWithSignature(finalState.address, provider)
                                .then(() => {
                                    console.log('✓ Authentication completed successfully')
                                    resolve()
                                })
                                .catch((err) => {
                                    console.error('✗ Authentication failed:', err)
                                    isProcessing.value = false
                                    connecting.value = false
                                    error.value = err.message || t('walletModalConnectionError')
                                })
                        } else {
                            console.log('✗ No address found - attempting account request fallback')

                            // Try to get provider first
                            let provider = modal.value.getWalletProvider()
                            if (!provider && modal.value.adapter) {
                                provider = modal.value.adapter.getProvider?.()
                            }
                            if (!provider && window.ethereum) {
                                provider = window.ethereum
                            }

                            if (provider && typeof provider.request === 'function') {
                                console.log('Requesting accounts via eth_requestAccounts...')
                                provider.request({ method: 'eth_requestAccounts' })
                                    .then((accounts) => {
                                        const addr = Array.isArray(accounts) ? accounts[0] : undefined
                                        if (addr) {
                                            console.log('✓ Account obtained from provider:', addr)
                                            connectingMessage.value = t('walletModalConnected') || 'Wallet connected!'
                                            statusMessage.value = t('walletModalAuthenticating') || 'Authenticating...'
                                            return directLoginWithSignature(addr, provider)
                                                .then(() => {
                                                    console.log('✓ Authentication completed successfully')
                                                    resolve()
                                                })
                                                .catch((err) => {
                                                    console.error('✗ Authentication failed after fallback:', err)
                                                    isProcessing.value = false
                                                    connecting.value = false
                                                    error.value = err.message || t('walletModalConnectionError')
                                                })
                                        }
                                        throw new Error('No accounts returned')
                                    })
                                    .catch((err) => {
                                        console.log('✗ Account request failed:', err)
                                        isProcessing.value = false
                                        connecting.value = false
                                        error.value = t('walletModalConnectionRejected') || 'Connection cancelled by user'
                                    })
                            } else {
                                console.log('✗ No provider available; treating as user rejection')
                                isProcessing.value = false
                                connecting.value = false
                                error.value = t('walletModalConnectionRejected') || 'Connection cancelled by user'
                            }
                        }
                    }, 300)
                })

                // Open the Reown AppKit modal AFTER subscribing so we don't miss the open event
                modal.value.open().then(() => {
                    console.log('Modal opened successfully')
                }).catch((err) => {
                    console.error('✗ Failed to open modal:', err)
                    unsubscribe()
                    isProcessing.value = false
                    connecting.value = false
                    error.value = err.message || t('walletModalConnectionError')
                })
            })
        }

        throw new Error(t('walletModalNotAvailable', {wallet: walletType}))

    } catch (err) {
        isProcessing.value = false
        connecting.value = false
        isModalOpen.value = false
        
        if (modal.value) {
            try {
                modal.value.close()
            } catch (e) {
                console.error('Error closing modal:', e)
            }
        }

        const normalizedMessage = (err?.message || '').toLowerCase()

        if (normalizedMessage.includes('user closed modal') || normalizedMessage.includes('user rejected') || normalizedMessage.includes('user disapproved')) {
            error.value = t('walletModalConnectionRejected') || 'Connection cancelled by user'
        } else if (normalizedMessage.includes('failed to get wallet provider')) {
            error.value = 'Failed to connect to wallet. Please try again.'
        } else if (normalizedMessage.includes('failed to fetch') || normalizedMessage.includes('blocked by client')) {
            error.value = t('walletModalWalletConnectBlocked') || 'WalletConnect was blocked by a browser extension. Please disable ad/tracker blocking for this site.'
        } else if (normalizedMessage.includes('project') && normalizedMessage.includes('not found')) {
            error.value = t('walletModalWalletConnectProjectInvalid') || 'WalletConnect project ID is invalid or not allowed for this domain.'
        } else if (normalizedMessage.includes('project id is missing')) {
            error.value = t('walletModalWalletConnectProjectMissing') || 'WalletConnect project ID is missing. Set VITE_WALLETCONNECT_PROJECT_ID.'
        } else if (err.code === 4001) {
            error.value = t('walletModalConnectionRejected')
        } else if (err.message && err.message.includes('timeout')) {
            error.value = t('walletModalConnectionTimeout')
        } else {
            error.value = err.message || t('walletModalConnectionError')
        }
    }
}

const cancelConnection = () => {
    isProcessing.value = false
    connecting.value = false
    selectedWallet.value = ''
    isModalOpen.value = false

    // Close Reown AppKit modal if open
    if (modal.value) {
        try {
            modal.value.close()
        } catch (err) {
            console.error('AppKit cancel error:', err)
        }
    }
}

// Ensure wallet session is fully cleared and disconnected
const disconnectWallet = async () => {
    try {
        console.log('Disconnecting wallet and clearing session...')
        // Attempt AppKit-specific disconnect/reset methods if available
        if (modal.value) {
            if (typeof modal.value.disconnect === 'function') {
                await modal.value.disconnect().catch(() => {})
            }
            if (typeof modal.value.reset === 'function') {
                await modal.value.reset().catch(() => {})
            }
            // Close modal to avoid lingering UI
            try { await modal.value.close() } catch {}
        }

        // Clear common WalletConnect/AppKit caches
        try {
            const keys = Object.keys(localStorage)
            keys.forEach((k) => {
                if (
                    k.includes('walletconnect') ||
                    k.includes('wc') ||
                    k.includes('appkit') ||
                    k.includes('w3m') ||
                    k.includes('web3modal')
                ) {
                    localStorage.removeItem(k)
                }
            })
        } catch {}

        // Also clear sessionStorage just in case
        try {
            const skeys = Object.keys(sessionStorage)
            skeys.forEach((k) => {
                if (
                    k.includes('walletconnect') ||
                    k.includes('wc') ||
                    k.includes('appkit') ||
                    k.includes('w3m') ||
                    k.includes('web3modal')
                ) {
                    sessionStorage.removeItem(k)
                }
            })
        } catch {}

        // Reset component UI state
        isProcessing.value = false
        connecting.value = false
        selectedWallet.value = ''
        isModalOpen.value = false
        error.value = null
        connectingMessage.value = ''
        statusMessage.value = ''

        console.log('✓ Wallet disconnected and caches cleared')
    } catch (err) {
        console.error('Failed to disconnect wallet:', err)
    }
}

const directLoginWithSignature = async (walletAddress, provider) => {
    try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const referralCode = getReferralCode()
        connectingMessage.value = t('walletModalGettingNonce') || 'Getting authentication challenge...'
        statusMessage.value = t('walletModalPleaseWait') || 'Please wait...'

        const nonceResponse = await Promise.race([
            fetch('/auth/web3/nonce', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    wallet_address: walletAddress
                })
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000))
        ])

        if (!nonceResponse.ok) {
            const errorData = await nonceResponse.json().catch(() => ({}))
            throw new Error(errorData.message || t('walletModalNonceFailed'))
        }

        const nonceData = await nonceResponse.json()

        if (!nonceData.success || !nonceData.nonce) {
            throw new Error(nonceData.message || t('walletModalNonceFailed'))
        }

        const nonce = nonceData.nonce
        connectingMessage.value = t('walletModalSigningMessage') || 'Requesting signature...'
        statusMessage.value = t('walletModalConfirmInWallet') || 'Please confirm in your wallet'

        const signature = await Promise.race([
            provider.request({
                method: 'personal_sign',
                params: [nonce, walletAddress]
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 60000))
        ])

        if (!signature) {
            throw new Error(t('walletModalSignatureFailed') || 'Signature failed')
        }

        connectingMessage.value = t('walletModalLoggingIn') || 'Logging in...'
        statusMessage.value = t('walletModalAuthenticatingWallet') || 'Authenticating...'

        const loginResponse = await Promise.race([
            fetch('/auth/web3/direct-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': csrfToken
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    wallet_address: walletAddress,
                    signature: signature,
                    wallet_type: 'walletconnect',
                    referral_code: referralCode
                })
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000))
        ])

        if (!loginResponse.ok) {
            const errorData = await loginResponse.json().catch(() => ({}))
            throw new Error(errorData.message || t('walletModalLoginFailed'))
        }

        const data = await loginResponse.json()

        if (!data.success) {
            throw new Error(data.message || t('walletModalLoginFailed'))
        }

        connectingMessage.value = t('walletModalSuccess') || 'Success!'
        statusMessage.value = t('walletModalRedirecting') || 'Redirecting...'

        await new Promise(resolve => setTimeout(resolve, 800))

        emit('success', data.user)

        window.location.href = data.redirect_url || '/user/dashboard'

    } catch (err) {
        console.error('Secure login error:', err)
        if (err.code === 4001) {
            throw new Error(t('walletModalSignatureRejected') || 'Signature request rejected')
        }

        throw err
    }
}

const handleEmailAuth = async () => {
    emailErrors.value = {}
    
    // Validation
    if (!email.value || !email.value.includes('@')) {
        emailErrors.value.email = 'Please enter a valid email address'
        return
    }
    
    if (!password.value || password.value.length < 8) {
        emailErrors.value.password = 'Password must be at least 8 characters'
        return
    }
    
    if (authMode.value === 'register') {
        if (password.value !== passwordConfirmation.value) {
            emailErrors.value.password_confirmation = 'Passwords do not match'
            return
        }
    }
    
    connecting.value = true
    selectedWallet.value = 'email'
    error.value = null
    
    try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const endpoint = authMode.value === 'login' ? '/auth/web3/email-login' : '/auth/web3/email-register'
        
        connectingMessage.value = authMode.value === 'login' 
            ? (t('walletModalLoggingIn') || 'Logging in...')
            : (t('walletModalCreatingAccount') || 'Creating account...')
        statusMessage.value = t('walletModalPleaseWait') || 'Please wait...'
        
        const requestBody = {
            email: email.value,
            password: password.value
        }
        
        if (authMode.value === 'register') {
            requestBody.password_confirmation = passwordConfirmation.value
            const referralCode = getReferralCode()
            if (referralCode) {
                requestBody.referral_code = referralCode
            }
        }
        
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': csrfToken
            },
            credentials: 'same-origin',
            body: JSON.stringify(requestBody)
        })
        
        const data = await response.json()
        
        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Authentication failed')
        }
        
        connectingMessage.value = t('walletModalSuccess') || 'Success!'
        statusMessage.value = t('walletModalRedirecting') || 'Redirecting...'
        
        await new Promise(resolve => setTimeout(resolve, 800))
        
        emit('success', data.user)
        
        window.location.href = data.redirect_url || '/user/dashboard'
        
    } catch (err) {
        console.error('Email auth error:', err)
        error.value = err.message || 'Authentication failed. Please try again.'
        connecting.value = false
        selectedWallet.value = ''
    }
}

const toggleAuthMode = () => {
    authMode.value = authMode.value === 'login' ? 'register' : 'login'
    emailErrors.value = {}
    passwordConfirmation.value = ''
}

const backToWallets = () => {
    showEmailForm.value = false
    email.value = ''
    password.value = ''
    passwordConfirmation.value = ''
    emailErrors.value = {}
    authMode.value = 'login'
    error.value = null
}

const retry = () => {
    error.value = null
    connecting.value = false
    isProcessing.value = false
    selectedWallet.value = ''
    showEmailForm.value = false
    email.value = ''
    password.value = ''
    passwordConfirmation.value = ''
    authMode.value = 'login'
    emailErrors.value = {}
}

const closeModal = () => {
    if (connecting.value) {
        cancelConnection()
    }

    showEmailForm.value = false
    email.value = ''
    password.value = ''
    passwordConfirmation.value = ''
    authMode.value = 'login'
    emailErrors.value = {}
    error.value = null
    emit('close')
}

const handleBackdropClick = () => {
    closeModal()
}

let initializationDone = false

onMounted(() => {
    if (initializationDone) return
    initializationDone = true

    setTimeout(() => {
        detectAvailableWallets()
        initAppKit().catch(err => {
            console.error('Failed to initialize Reown AppKit:', err)
        })
    }, 500)
})

onUnmounted(() => {
    initializationDone = false
    isProcessing.value = false
    connecting.value = false
    isModalOpen.value = false
    
    // Reown AppKit handles its own cleanup
})
</script>

<style scoped>
@keyframes fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes scale-in {
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes pulse-slow {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

.animate-fade-in {
    animation: fade-in 0.3s ease-out;
}

.animate-scale-in {
    animation: scale-in 0.3s ease-out;
}

.animate-pulse-slow {
    animation: pulse-slow 3s ease-in-out infinite;
}

.backdrop-blur-2xl {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

.wallet-svg {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
}

.wallet-svg svg {
    width: 100%;
    height: 100%;
}

@media (prefers-reduced-motion: reduce) {
    .animate-fade-in,
    .animate-scale-in,
    .animate-pulse-slow {
        animation: none !important;
    }
}
</style>
